import React, { useState, useEffect } from 'react';
import {
  ArrowsRightLeftIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  ArrowPathIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useToast } from '../Toast';
import {
  colaboradoresService,
  type Colaborador,
} from '../../services/colaboradores.service';
import type { TransferirColaboradoresLoteResponseContract } from '@sstexames/shared-types';
import { formatCPF, formatCNPJ } from '../../utils/formatters';

export interface EmpresaFilialItem {
  id: number;
  nome?: string | null;
  razao_social?: string | null;
  cnpj?: string | null;
  cidade?: string | null;
  uf?: string | null;
}

export interface TransferirColaboradoresModalProps {
  isOpen: boolean;
  onClose: () => void;
  empresaOrigemId: number;
  empresaOrigemNome: string;
  empresaOrigemCnpj?: string;
  colaboradores: Colaborador[];
  filiaisDisponiveis?: EmpresaFilialItem[];
  onSuccess: (result: TransferirColaboradoresLoteResponseContract) => void;
}

export default function TransferirColaboradoresModal({
  isOpen,
  onClose,
  empresaOrigemId,
  empresaOrigemNome,
  empresaOrigemCnpj,
  colaboradores,
  filiaisDisponiveis: filiaisProp,
  onSuccess,
}: TransferirColaboradoresModalProps) {
  const toast = useToast();
  const [empresaDestinoId, setEmpresaDestinoId] = useState<number | null>(null);
  const [motivo, setMotivo] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [filiais, setFiliais] = useState<EmpresaFilialItem[]>(filiaisProp || []);
  const [loadingFiliais, setLoadingFiliais] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen) return;

    if (filiaisProp && filiaisProp.length > 0) {
      setFiliais(filiaisProp);
      setEmpresaDestinoId(filiaisProp[0].id);
      return;
    }

    // Buscar filiais do grupo econômico da empresa atual
    let isMounted = true;
    setLoadingFiliais(true);
    colaboradoresService
      .getFiliais()
      .then((res) => {
        if (!isMounted) return;
        const peers = res?.filiais || [];
        setFiliais(peers);
        if (peers.length > 0) {
          setEmpresaDestinoId(peers[0].id);
        }
      })
      .catch((err) => {
        console.warn('Não foi possível buscar filiais via grupo_economico:', err);
      })
      .finally(() => {
        if (isMounted) setLoadingFiliais(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, empresaOrigemId, filiaisProp]);

  if (!isOpen) return null;

  const total = colaboradores.length;
  const filialSelecionada = filiais.find((f) => f.id === empresaDestinoId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!empresaDestinoId) {
      toast.error('Selecione a unidade/filial de destino do mesmo Grupo Econômico.');
      return;
    }
    if (total === 0) {
      toast.error('Nenhum colaborador selecionado para transferência.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await colaboradoresService.transferirEmLote({
        colaboradores_ids: colaboradores.map((c) => c.id),
        empresa_origem_id: empresaOrigemId,
        empresa_destino_id: empresaDestinoId,
        motivo: motivo.trim() || undefined,
      });

      toast.success(
        response.mensagem ||
          `${total} colaborador(es) transferido(s) com sucesso para ${
            filialSelecionada?.nome || filialSelecionada?.razao_social || 'a filial de destino'
          }!`,
      );

      onSuccess(response);
      onClose();
    } catch (err: unknown) {
      let message = 'Erro ao processar transferência de colaboradores.';
      if (err && typeof err === 'object' && 'response' in err) {
        const resp = (err as { response?: { data?: { message?: string } } }).response;
        if (resp?.data?.message) message = resp.data.message;
      } else if (err instanceof Error) {
        message = err.message;
      }
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 py-5 bg-gradient-to-r from-emerald-700 via-teal-700 to-cyan-800 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/10 rounded-xl backdrop-blur-xs border border-white/20">
              <ArrowsRightLeftIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold tracking-tight">
                Transferência de Colaborador(es)
              </h3>
              <p className="text-xs text-emerald-100/90">
                Remanejamento entre unidades e filiais do mesmo Grupo Econômico
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Card Comparativo Origem -> Destino */}
          <div className="bg-gray-50/80 rounded-xl p-4 border border-gray-200/80">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative">
              {/* Origem */}
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-2xs">
                <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider block mb-1">
                  Unidade Atual (Origem)
                </span>
                <p className="font-bold text-gray-900 text-sm truncate" title={empresaOrigemNome}>
                  {empresaOrigemNome}
                </p>
                {empresaOrigemCnpj && (
                  <p className="text-xs text-gray-500 font-mono mt-0.5">
                    {formatCNPJ(empresaOrigemCnpj)}
                  </p>
                )}
              </div>

              {/* Destino */}
              <div className="p-3 bg-emerald-50/50 rounded-lg border border-emerald-200 shadow-2xs">
                <label className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider block mb-1">
                  Filial de Destino (Mesmo Grupo) *
                </label>
                {loadingFiliais ? (
                  <div className="flex items-center gap-2 py-1 text-xs text-emerald-700">
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    <span>Carregando filiais...</span>
                  </div>
                ) : filiais.length === 0 ? (
                  <div className="flex items-start gap-1.5 py-1 text-xs text-amber-700">
                    <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <span>Nenhuma outra filial encontrada neste Grupo Econômico.</span>
                  </div>
                ) : (
                  <select
                    value={empresaDestinoId || ''}
                    onChange={(e) => setEmpresaDestinoId(Number(e.target.value))}
                    disabled={isSubmitting}
                    className="w-full text-xs font-semibold text-gray-900 bg-white border border-emerald-300 rounded-md p-1.5 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-hidden"
                  >
                    {filiais.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.nome || f.razao_social}
                        {f.cnpj ? ` (${formatCNPJ(f.cnpj)})` : ''}
                        {f.cidade && f.uf ? ` - ${f.cidade}/${f.uf}` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>

          {/* Colaboradores Selecionados */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <UserGroupIcon className="w-4 h-4 text-emerald-600" />
                <span>Colaboradores Selecionados ({total})</span>
              </label>
            </div>
            <div className="max-h-44 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-xl bg-gray-50/50">
              {colaboradores.map((c) => (
                <div key={c.id} className="p-2.5 px-3 flex items-center justify-between text-xs">
                  <div>
                    <span className="font-semibold text-gray-900">{c.nome}</span>
                    <span className="text-gray-500 font-mono ml-2">({formatCPF(c.cpf)})</span>
                  </div>
                  <div className="text-gray-500 text-[11px] text-right">
                    <span>{c.setor?.nome || 'Sem Setor'}</span>
                    <span className="mx-1">•</span>
                    <span>{c.cargo?.nome || 'Sem Cargo'}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Motivo da Transferência */}
          <div>
            <label className="text-xs font-bold text-gray-700 block mb-1">
              Motivo ou Justificativa da Transferência (Opcional)
            </label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ex: Remanejamento operacional para nova frente de trabalho, abertura de unidade ou demanda de equipe..."
              rows={2}
              maxLength={255}
              disabled={isSubmitting}
              className="w-full text-xs text-gray-800 border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-hidden"
            />
            <span className="text-[10px] text-gray-400 block text-right mt-0.5">
              {motivo.length}/255 caracteres
            </span>
          </div>

          {/* Aviso Inteligente de SST */}
          <div className="p-3.5 bg-emerald-50/60 border border-emerald-200 rounded-xl flex items-start gap-3">
            <ShieldCheckIcon className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="text-xs text-emerald-950 space-y-1">
              <p className="font-bold">Regras e Automação de SST:</p>
              <ul className="list-disc pl-4 space-y-0.5 text-[11px] text-emerald-900/90 leading-relaxed">
                <li>
                  <strong>Setores e Cargos:</strong> Se o setor ou cargo não existir na filial de destino, o sistema cria automaticamente com os mesmos riscos ocupacionais (NR-7).
                </li>
                <li>
                  <strong>Histórico Preservado:</strong> O histórico de ASOs, exames anteriores e biometrias permanece intacto vinculado ao CPF do colaborador.
                </li>
              </ul>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || filiais.length === 0 || total === 0}
              className="px-5 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg shadow-sm flex items-center gap-2 transition-colors cursor-pointer"
            >
              {isSubmitting ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                  <span>Transferindo colaboradores...</span>
                </>
              ) : (
                <>
                  <ArrowsRightLeftIcon className="w-4 h-4" />
                  <span>Confirmar Transferência ({total})</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
